/**
 * menu.ts
 *
 * NE İŞE YARAR : Sol menünün tanımı — hangi öğe hangi izinle görünür
 * KAYNAK SPEC  : 15-arayuz-standardi.md §5.2 (menü role göre kurulur) · §7.1 (yetkisi
 *                olmayan hiç görmez) · §7.2 (izinler `X-02`den) · §7.3
 *                04-api-sozlesmesi.md §4 registry · 02-rol-izin-matrisi.md §4 (izin kataloğu)
 * BAĞLI OLDUĞU : src/metin/tr.ts (başlıklar) · @tabler/icons-react
 * KULLANAN     : src/kabuk/kabuk.tsx
 * DİKKAT       : 1) MENÜ **İZNE** BAKAR, ROL ADINA DEĞİL (ADR-008 §1 · `15` §7.2).
 *                   `15` §5.2'nin rol tablosu bir **örnektir**, tanım değil: rol adına
 *                   göre yazılsaydı yeni bir rol eklendiğinde bu dosyanın da
 *                   güncellenmesi gerekirdi ve unutulduğunda o rol boş bir menü görürdü.
 *                   İzne bakınca yeni rol, izinleri neyse onu görür.
 *                2) İZNİ OLMAYAN ÖĞEYİ **HİÇ GÖRMEZ** (`15` §7.1). Soluk ya da pasif
 *                   gösterilmez: kabul edilen sonuç, kullanıcının sistemin başka nelere
 *                   sahip olduğunu görememesidir.
 *                3) BU BİR GÜVENLİK SINIRI DEĞİLDİR (`15` §7.3). Menüyü gizlemek işlemi
 *                   engellemez; engelleyen şey sunucudaki izin kontrolü ve RLS'tir. Bu
 *                   dosya yanlış yazılsa bile veri sızmaz — yalnız kullanıcı yanlış
 *                   yere tıklar ve `403` alır.
 *                4) HER ÖĞENİN İZNİ, O EKRANI BESLEYEN **LİSTE UCUNUN** İZNİDİR. Ekran
 *                   açılıp da ilk isteğinde `403` almasın diye; farklı bir izin
 *                   yazılsaydı menü *"girebilirsin"* der, ekran *"giremezsin"* derdi.
 *                5) MENÜDEKİ HER ÖĞENİN ARTIK GERÇEK BİR EKRANI VAR (19. ve 20. adımlar
 *                   bitti — 2026-08-31 06:05:00). 15. adımda konan geçici *"bu ekran
 *                   yazılmadı"* sayfası kaldırıldı.
 *                6) *"Rıza kayıtları"* VE *"Veliler"* MENÜDE YOK — ikisi de aynı sebeple.
 *                   `15` §5.2 rıza kayıtlarını `KVKK_SORUMLUSU` için sayıyor ama
 *                   **çapraz liste ucu yok**: `consent_record` yalnız
 *                   `GET /persons/{id}/consents`, `guardian_relation` yalnız
 *                   `GET /persons/{id}/guardians` ile okunuyor. Menüye konsalardı
 *                   hiçbir zaman doldurulamayacak bir sayfaya çıkarlardı. İkisi de
 *                   **kişi ekranındadır** (`ekran/kisi/kisi-detay.tsx` notu 4) ve veri
 *                   zaten oraya bağlı. *"Veliler"* öğesi 20. adımda kaldırıldı; 15.
 *                   adımda konmuştu ve ekranı yazılmaya çalışılınca ucun olmadığı
 *                   görüldü.
 * SON GÜNCELLEME: 2026-08-31 06:05:00
 */
import {
  IconBed,
  IconCalendarTime,
  IconChecklist,
  IconClockHour4,
  IconDoorEnter,
  IconBuildingCommunity,
  IconCertificate,
  IconClipboardList,
  IconFileSearch,
  IconHome2,
  IconKey,
  IconShieldLock,
  IconUserCheck,
  IconUsers,
  IconUsersGroup,
  type Icon,
} from '@tabler/icons-react';

import type { MetinAnahtari } from '../metin';

export interface MenuOgesi {
  /** Başlık metni sözlükten gelir; burada anahtarı durur (`15` §14). */
  readonly baslik: MetinAnahtari;
  readonly yol: string;
  readonly ikon: Icon;
  /**
   * Öğenin görünmesi için gereken izin kodu — besleyen liste ucunun izni (not 4).
   *
   * `null`, *"izin gerektirmez"* demektir ve yalnız `SELF` uçları için kullanılır.
   */
  readonly izin: string | null;
}

export interface MenuGrubu {
  readonly baslik: MetinAnahtari;
  readonly ogeler: readonly MenuOgesi[];
}

/**
 * Menü tanımı.
 *
 * Sıra bilinçlidir: kullanıcının gün içinde en sık dokunduğu iş en üsttedir. `15` §5.2
 * menüyü *"modül listesi değil, iş listesi"* sayıyor — bu yüzden gruplar modül
 * numaralarına göre değil, işin akışına göre dizildi: aday gelir (başvuru), kişi olur,
 * öğrenci olur, bir yatağa yerleşir.
 */
export const MENU: readonly MenuGrubu[] = [
  {
    baslik: 'menu.grupKayit',
    ogeler: [
      { baslik: 'menu.basvurular', yol: '/basvurular', ikon: IconClipboardList, izin: 'application.read' },
      { baslik: 'menu.basvuruDonemleri', yol: '/basvuru-donemleri', ikon: IconCertificate, izin: 'application_period.read' },
      { baslik: 'menu.kisiler', yol: '/kisiler', ikon: IconUsers, izin: 'person.read' },
      { baslik: 'menu.ogrenciler', yol: '/ogrenciler', ikon: IconUsersGroup, izin: 'student.read' },
    ],
  },
  {
    // Faz 2 (98.–101. adımlar) — nöbetçinin gün içinde en sık dokunduğu işler.
    // Grup **en üstte değil ikinci sırada**: kayıt akışı (başvuru → kişi → öğrenci)
    // hâlâ sistemin girişidir; günlük operasyon onun üzerine biner.
    baslik: 'menu.grupGunluk',
    ogeler: [
      { baslik: 'menu.gecisler', yol: '/gecisler', ikon: IconDoorEnter, izin: 'passage.read' },
      { baslik: 'menu.izinler', yol: '/izinler', ikon: IconCalendarTime, izin: 'leave.read' },
      { baslik: 'menu.yoklamalar', yol: '/yoklamalar', ikon: IconChecklist, izin: 'roll_call.read' },
      { baslik: 'menu.ziyaretler', yol: '/ziyaretler', ikon: IconUserCheck, izin: 'visit.read' },
      // Saat ayarı ayrı bir izindir (`420` notu 1): yoklamayı açan, yurdun düzenini
      // değiştiremez. Menüde de ayrı durur; aynı öğeye konsaydı iki yetki tek yerde
      // duruyormuş gibi görünürdü.
      {
        baslik: 'menu.yoklamaSaatleri',
        yol: '/yoklama-saatleri',
        ikon: IconClockHour4,
        izin: 'roll_call.schedule',
      },
    ],
  },
  {
    baslik: 'menu.grupYerlesim',
    ogeler: [
      { baslik: 'menu.yerlesimler', yol: '/yerlesimler', ikon: IconBed, izin: 'placement.read' },
      { baslik: 'menu.yapi', yol: '/yapi', ikon: IconHome2, izin: 'building.read' },
    ],
  },
  {
    baslik: 'menu.grupKurum',
    ogeler: [
      { baslik: 'menu.kurumlar', yol: '/kurumlar', ikon: IconBuildingCommunity, izin: 'organization.read' },
    ],
  },
  {
    baslik: 'menu.grupYonetim',
    ogeler: [
      { baslik: 'menu.kullanicilar', yol: '/kullanicilar', ikon: IconUsers, izin: 'user.read' },
      { baslik: 'menu.roller', yol: '/roller', ikon: IconKey, izin: 'role.read' },
      { baslik: 'menu.oturumlar', yol: '/oturumlar', ikon: IconShieldLock, izin: 'session.read' },
    ],
  },
  {
    baslik: 'menu.grupDenetim',
    ogeler: [
      { baslik: 'menu.denetim', yol: '/denetim', ikon: IconFileSearch, izin: 'audit.read' },
      { baslik: 'menu.guvenlikOlaylari', yol: '/guvenlik-olaylari', ikon: IconShieldLock, izin: 'security_event.read' },
      { baslik: 'menu.veriTalepleri', yol: '/veri-talepleri', ikon: IconClipboardList, izin: 'dsr.read' },
      { baslik: 'menu.hukukiMuhafaza', yol: '/hukuki-muhafaza', ikon: IconShieldLock, izin: 'legal_hold.manage' },
    ],
  },
];

/**
 * İzin kümesine göre menüyü süzer.
 *
 * Öğesi kalmayan grup **hiç çizilmez**: başlığı duran boş bir grup, kullanıcıya orada
 * göremediği bir şey olduğunu söylerdi (`15` §7.1).
 */
export function menuyuSuz(izinVar: (kod: string) => boolean): readonly MenuGrubu[] {
  return MENU.map((grup) => ({
    ...grup,
    ogeler: grup.ogeler.filter((oge) => oge.izin === null || izinVar(oge.izin)),
  })).filter((grup) => grup.ogeler.length > 0);
}
